// ============================================================
// PlatformAnalyticsPage.js
// Route /platform/analytics — internal A2Z IT tooling. Four cards:
//   1. Active Clients and Users
//   2. AI Agent Call Volumes — last 3 months
//   3. Notification Send Volumes — last 3 months
//   4. On-Demand Narrative Refresh Log (24h abuse flag)
//
// Functional > pretty. No charts in v1 — tables suffice.
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import PageHeader from '../components/PageHeader';
import Badge from '../components/Badge';
import {
  getAnalyticsOverview, getAnalyticsAgentVolumes,
  getAnalyticsNotificationVolumes, getAnalyticsNarrativeRefreshes,
} from '../api/adminAnalytics';
import { translateError } from '../utils/errorMessages';
import { formatDate } from '../utils/format';
import StatusMessage from '../components/StatusMessage';
import './PlatformPages.css';
import './PlatformAnalyticsPage.css';

function Card({ title, children, error, loading }) {
  return (
    <section className="analytics-card">
      <h2 className="analytics-card__title">{title}</h2>
      {loading && <StatusMessage as="p" variant="loading" className="analytics-card__state">Loading…</StatusMessage>}
      {error && <StatusMessage as="p" variant="error" className="analytics-card__state analytics-card__state--error">{error}</StatusMessage>}
      {!loading && !error && children}
    </section>
  );
}

export default function PlatformAnalyticsPage() {
  const [overview, setOverview] = useState({ data: null, loading: true, error: null });
  const [agents,   setAgents]   = useState({ data: null, loading: true, error: null });
  const [notifs,   setNotifs]   = useState({ data: null, loading: true, error: null });
  const [refresh,  setRefresh]  = useState({ data: null, loading: true, error: null });

  const fetchAll = useCallback(async () => {
    const wrap = async (fn, setter) => {
      setter({ data: null, loading: true, error: null });
      try { setter({ data: await fn(), loading: false, error: null }); }
      catch (err) { setter({ data: null, loading: false, error: translateError(err) }); }
    };
    // Independent — fire in parallel.
    wrap(() => getAnalyticsOverview(),               setOverview);
    wrap(() => getAnalyticsAgentVolumes(3),          setAgents);
    wrap(() => getAnalyticsNotificationVolumes(3),   setNotifs);
    wrap(() => getAnalyticsNarrativeRefreshes(50),   setRefresh);
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  return (
    <>
      <PageHeader
        title="Platform Analytics"
        subtitle="Internal A2Z IT tooling. Read-only aggregations across all clients."
      />

      <div className="analytics-grid">
        {/* Card 1 — Active clients + users */}
        <Card title="Active Clients and Users" loading={overview.loading} error={overview.error}>
          {overview.data && (
            <div className="analytics-overview">
              <div className="analytics-overview__big">
                <div className="analytics-overview__value">{overview.data.activeClients}</div>
                <div className="analytics-overview__label">Active clients</div>
              </div>
              <div className="analytics-overview__big">
                <div className="analytics-overview__value">{overview.data.activeUsers}</div>
                <div className="analytics-overview__label">Active users</div>
              </div>
              <div className="analytics-overview__breakdown">
                <div><strong>{overview.data.byRole.manager}</strong> Managers</div>
                <div><strong>{overview.data.byRole.carer}</strong> Carers</div>
                <div><strong>{overview.data.byRole.admin}</strong> Admins</div>
                <div><strong>{overview.data.byRole.auditor}</strong> Auditors</div>
              </div>
            </div>
          )}
        </Card>

        {/* Card 2 — Agent volumes */}
        <Card title="AI Agent Call Volumes — last 3 months" loading={agents.loading} error={agents.error}>
          {agents.data && (
            <VolumesTable
              months={agents.data.months}
              items={agents.data.items}
              labelKey="moduleName"
              codeKey="moduleKey"
              emptyMessage="No agent calls in the window."
            />
          )}
        </Card>

        {/* Card 3 — Notification volumes */}
        <Card title="Notification Send Volumes — last 3 months" loading={notifs.loading} error={notifs.error}>
          {notifs.data && (
            <VolumesTable
              months={notifs.data.months}
              items={notifs.data.items}
              labelKey="code"
              codeKey="code"
              emptyMessage="No notifications sent in the window."
            />
          )}
        </Card>

        {/* Card 4 — Narrative refresh log */}
        <Card title="On-Demand Narrative Refresh Log" loading={refresh.loading} error={refresh.error}>
          {refresh.data && (
            <table className="analytics-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Requested By</th>
                  <th>Date / Time</th>
                  <th style={{ textAlign: 'right' }}>Tokens</th>
                  <th>Outcome</th>
                </tr>
              </thead>
              <tbody>
                {refresh.data.items.length === 0 && (
                  <tr><td colSpan={5} style={{ textAlign: 'center', color: '#888' }}>No refreshes yet.</td></tr>
                )}
                {refresh.data.items.map((r) => (
                  <tr
                    key={r.logId}
                    className={r.abuseFlag ? 'analytics-table__row--abuse' : ''}
                    title={r.abuseFlag ? `${r.last24hCount} refreshes by this client in last 24h` : ''}
                  >
                    <td>{r.clientName}</td>
                    <td>{r.requestedBy}</td>
                    <td>{formatDate(r.requestedAt) || '—'}</td>
                    <td style={{ textAlign: 'right' }}>{r.totalTokens || 0}</td>
                    <td>
                      <Badge variant={r.outcome === 'success' ? 'success' : 'danger'}>{r.outcome}</Badge>
                      {r.abuseFlag && <span className="platform-page__warn"> ⚠ {r.last24hCount}/24h</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </>
  );
}

function VolumesTable({ months, items, labelKey, codeKey, emptyMessage }) {
  if (!items || items.length === 0) {
    return <p style={{ color: '#888', textAlign: 'center', padding: 16 }}>{emptyMessage}</p>;
  }
  return (
    <table className="analytics-table">
      <thead>
        <tr>
          <th>{labelKey === 'code' ? 'Notification Type' : 'Agent'}</th>
          {months.map((m) => <th key={m} style={{ textAlign: 'right' }}>{m}</th>)}
          <th style={{ textAlign: 'right' }}>Total</th>
        </tr>
      </thead>
      <tbody>
        {items.map((it) => (
          <tr key={it[codeKey]}>
            <td>{it[labelKey] || it[codeKey]}</td>
            {months.map((m) => <td key={m} style={{ textAlign: 'right' }}>{it.monthly[m] || 0}</td>)}
            <td style={{ textAlign: 'right', fontWeight: 600 }}>{it.total}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
